import React, { Dispatch, SetStateAction, useState } from "react";
import useMobile from "../../hooks/useMobile";
import { Link } from "react-router-dom";
import DeletePopUp from "../DeletePopUp";
import { IComplaint } from "../../exports/interfaces";
import { useLoader } from "../../context/LoaderContext";
import { fetchComplaints } from "../../api/ComplaintApi";
import { dynamicFailed, dynamicSuccess } from "../../hooks/useToast";
import axiosClient from "../../axioxClient.ts/axiosClient";
import { Eye, Pencil } from "lucide-react";
import { useOption } from "../../context/OptionContext";
import { usePermission } from "../../hooks/usePermission";
import { darkenColor } from "../../exports/functions";

const getStatusClass = (status: string) => {
  switch (status) {
    case "Urgent":
      return "pill-danger";
    case "Not Urgent":
      return "pill-primary";
    case "Regular":
      return "pill-info";
    default:
      return "bg-secondary";
  }
};

const ComplaintTable = ({
  viewAll,
  complaint,
  setComplaint,
}: {
  viewAll?: boolean;
  complaint: IComplaint[];
  setComplaint: Dispatch<SetStateAction<IComplaint[]>>;
}) => {
  const { setLoading } = useLoader();
  const { statusOptions } = useOption();
  const { canEdit, canDelete } = usePermission();
  const isMobile = useMobile();
  const [showDelete, setShowDelete] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = viewAll ? 10 : 5;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = complaint.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(complaint.length / itemsPerPage);
  const handleClose = () => {
    setShowDelete(false);
  };

  const statusStyle = (status: string) => {
    const cooperativeStatuses =
      statusOptions?.find((item) => item.name === "Complaint Statuses")
        ?.statusOptions || [];

    const matchedStatus = cooperativeStatuses.find(
      (item) => item.label === status,
    );

    return {
      backgroundColor: matchedStatus?.color || "#ccc",
      color: darkenColor(matchedStatus?.color || "#ccc", 40),
    };
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      if (deleteId === null) return;

      const response = await axiosClient.delete(`/complaints/${deleteId}`);
      if (response.status === 200 || response.status === 201) {
        fetchComplaints()
          .then((complaintData) => {
            setComplaint(complaintData);
            setShowDelete(false);
            setDeleteId(null);
            setTimeout(() => {
              setLoading(false);
              dynamicSuccess("Complaint deleted successfully!");
            }, 1000);
          })
          .catch((error) => {
            setTimeout(() => {
              setLoading(false);
              dynamicFailed(`${error.message}`);
            }, 1000);
          });
      }
    } catch (error: any) {
      setTimeout(() => {
        dynamicFailed(`${error.message}`);
        setLoading(false);
      }, 1000);
    }
  };
  return (
    <>
      <div className="dashboard-card">
        <div className="card-body">
          {!viewAll && <h6 className="mb-3">LATEST REGISTERED</h6>}

          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th>S. no.</th>
                  <th>Complain Title</th>
                  <th>Cooperative</th>
                  <th>Category</th>
                  <th>Filed on</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th className="sticky-action">Actions</th>
                </tr>
              </thead>

              <tbody>
                {currentData.map((item, index) => (
                  <tr key={index}>
                    <td className="text-danger">
                      {" "}
                      {String(indexOfFirstItem + index + 1).padStart(6, "0")}
                    </td>
                    <td>{item.complainTitle}</td>
                    <td>{item.cooperativeName}</td>
                    <td>{item.complaintType}</td>
                    <td>{item.filedOn}</td>
                    <td>
                      {" "}
                      <span
                        className={`badge rounded-pill px-3 py-2 ${getStatusClass(
                          item.complaintPriority,
                        )}`}
                      >
                        {item.complaintPriority}
                      </span>
                    </td>

                    <td>
                      <span
                        className={`badge rounded-pill px-3 py-2`}
                        style={statusStyle(item.status || "")}
                      >
                        {item.status}
                      </span>
                    </td>

                    <td className="sticky-action">
                      <div className="d-flex gap-3 align-items-center">
                        <Link to={`/complaints/view/${item.id}`}>
                          <i
                            className="cursor-pointer"
                            data-toggle="tooltip"
                            data-placement="top"
                            title="View"
                          >
                            <Eye />
                          </i>
                        </Link>
                        <Link
                        to={
                          canEdit("complaints")
                            ? `/complaints/edit/${item.id}`
                            : "#"
                        }
                        onClick={(e) => {
                          if (!canEdit("complaints")) {
                            e.preventDefault();
                          }
                        }}
                        style={{
                          pointerEvents: canEdit("complaints")
                            ? "auto"
                            : "none",
                          opacity: canEdit("complaints") ? 1 : 0.5,
                        }}
                      >
                        <i
                          data-toggle="tooltip"
                          data-placement="top"
                          title={
                            canEdit("complaints") ? "Edit" : "No permission"
                          }
                        >
                          <Pencil size={20} />
                        </i>
                      </Link>
                      <i
                        className={`fa-solid fa-trash text-danger ${
                          canDelete("complaints")
                            ? "cursor-pointer"
                            : "opacity-50"
                        }`}
                        data-toggle="tooltip"
                        data-placement="top"
                        title={
                          canDelete("complaints")
                            ? "Delete"
                            : "No permission"
                        }
                        style={{
                          pointerEvents: canDelete("complaints")
                            ? "auto"
                            : "none",
                        }}
                        onClick={() => {
                          if (!canDelete("complaints")) return;

                          setDeleteId(item?.id || 0);
                          setShowDelete(true);
                        }}
                      ></i>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div
          className={`d-flex justify-content-between align-items-center flex-wrap ${isMobile ? "gap-2" : ""}`}
        >
          <small>
            {indexOfFirstItem + 1}–{Math.min(indexOfLastItem, complaint.length)}{" "}
            of {complaint.length} Complaints
          </small>

          <div className="pagination">
            <button
              className="pagination-arrow"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
            >
              <i className="fa-solid fa-angle-left"></i>
            </button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                className={`pagination-page ${
                  currentPage === i + 1 ? "active" : ""
                }`}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            <button
              className="pagination-arrow"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
            >
              <i className="fa-solid fa-angle-right"></i>
            </button>
          </div>
        </div>
      </div>
      {showDelete && (
        <DeletePopUp onClose={handleClose} onConfirm={handleDelete} />
      )}
    </>
  );
};

export default ComplaintTable;
